// api-core.js — the core Stride REST facade used by the whole frontend.
// Every method here targets the Stride/Supabase backend (routes in
// modules/database/router.js plus module sub-routers). Responses stay
// Jira-shaped (issue.fields, customfield_*, ADF) by convention, but there
// is no Jira behind them — Stride is the only backend.
import { client, API_BASE } from './api-client';

// Only the keys /api/users reads for a field-scoped picker; empty ones dropped.
export function userScopeParams(scope) {
  const out = {};
  if (!scope || !scope.fieldId) return out;
  for (const k of ['fieldId', 'projectKey', 'projectId', 'issueTypeId', 'issueKey']) {
    if (scope[k] != null && String(scope[k]) !== '') out[k] = String(scope[k]);
  }
  return out;
}

const jiraApi = {

  // Auth (pass-through to auth module endpoints)
  async getMe() {
    console.log('api.getMe: calling', API_BASE + '/api/auth/me');
    const response = await client.get('/api/auth/me');
    console.log('api.getMe: response', response.data);
    return response.data;
  },

  // Self-service profile — the signed-in person's own display name + avatar.
  async getProfile() {
    const response = await client.get('/api/profile');
    return response.data;
  },
  async updateProfile({ displayName }) {
    const response = await client.patch('/api/profile', { displayName });
    return response.data;
  },
  async uploadProfileAvatar(dataUrl) {
    const response = await client.post('/api/profile/avatar', { dataUrl });
    return response.data;
  },
  async removeProfileAvatar() {
    const response = await client.delete('/api/profile/avatar');
    return response.data;
  },

  // Search & Issues
  async searchIssues(jql, fields = '*all', maxResults = 100, siteUrl = null, startAt = 0, skipTotal = false) {
    const body = { jql, fields, maxResults };
    if (siteUrl) body.siteUrl = siteUrl;
    if (startAt) body.startAt = startAt;
    // skipTotal → backend drops the parallel COUNT (for callers that only
    // need the rows, e.g. the top-bar quick-search dropdown).
    if (skipTotal) body.skipTotal = true;
    const response = await client.post('/api/search', body);
    return response.data;
  },

  async countIssues(jql, siteUrl = null) {
    const body = { jql };
    if (siteUrl) body.siteUrl = siteUrl;
    const response = await client.post('/api/search/count', body);
    return response.data?.count ?? 0;
  },

  async getIssue(key, fields = '*all', siteUrl = null) {
    const params = { fields };
    if (siteUrl) params.siteUrl = siteUrl;
    const response = await client.get(`/api/issue/${key}`, { params });
    return response.data;
  },

  async getEditMeta(key, siteUrl = null) {
    const params = {};
    if (siteUrl) params.siteUrl = siteUrl;
    const response = await client.get(`/api/issue/${key}/editmeta`, { params });
    return response.data;
  },

  async getIssueLayout(projectId, issueTypeId, { refresh = false, context = 'view' } = {}) {
    const params = {};
    if (refresh) params.refresh = 'true';
    if (context !== 'view') params.context = context;
    const response = await client.get(`/api/issue-layout/${projectId}/${issueTypeId}`, { params });
    return response.data;
  },

  async getScreenLayout(projectId, issueTypeId, operation = 'edit', refresh = false) {
    const response = await client.get(`/api/screen-layout/${projectId}/${issueTypeId}`, {
      params: { operation, ...(refresh ? { refresh: 'true' } : {}) }
    });
    return response.data;
  },

  async updateIssue(key, fields, siteUrl = null) {
    const body = { fields };
    if (siteUrl) body.siteUrl = siteUrl;
    const response = await client.put(`/api/issue/${key}`, body);
    return response.data;
  },

  async createIssue(data) {
    const response = await client.post('/api/issue', data);
    return response.data;
  },

  // Project creation is served from the cloned dataset (a new
  // zebra_stride_space row) — see modules/folders/server/stride/
  // project-create-handler.js. data: { name, key, categoryName? }.
  async createProject(data) {
    const response = await client.post('/api/create-project', data);
    return response.data;
  },

  // Returns Jira's /projectvalidate/key shape: { errorMessages: [...] }
  // populated when the key is taken, empty when it's free.
  async checkProjectKeyAvailable(key) {
    const response = await client.get(`/api/project-key-available/${encodeURIComponent(key)}`);
    return response.data;
  },

  async getTransitions(key, siteUrl = null) {
    const params = {};
    if (siteUrl) params.siteUrl = siteUrl;
    const response = await client.get(`/api/issue/${key}/transitions`, { params });
    return response.data;
  },

  async transitionIssue(key, transitionId, siteUrl = null, fields = null, comment = null) {
    const body = { transitionId };
    if (siteUrl) body.siteUrl = siteUrl;
    if (fields != null) body.fields = fields;       // transition-screen field values
    if (comment != null) body.comment = comment;    // transition-screen comment
    const response = await client.post(`/api/issue/${key}/transition`, body);
    return response.data;
  },

  // Fields on a transition's screen (editmeta-shaped) for the transition-screen
  // modal. Returns { hasScreen, fields }.
  async getTransitionScreen(key, transitionId) {
    const response = await client.get(`/api/issue/${key}/transition/${transitionId}/screen`);
    return response.data;
  },

  async getFields(siteUrl) {
    const response = await client.get('/api/fields', { params: { siteUrl } });
    return response.data;
  },

  async getCreateMeta(projectKey, issueTypeId) {
    const response = await client.get(`/api/createmeta/${projectKey}/${issueTypeId}`);
    return response.data;
  },

  // List every project where the current user can create the given issue
  // type. Used by the WIC end-user form's project picker.
  async getProjectsForIssueType(issueTypeId) {
    const response = await client.get(`/api/projects-for-issuetype/${encodeURIComponent(issueTypeId)}`);
    return response.data;
  },

  async getFieldOptions(fieldId, projectKey, issueTypeId, issueKey) {
    const response = await client.get('/api/field-options', {
      params: { fieldId, projectKey, issueTypeId, issueKey }
    });
    return response.data;
  },

  // `scope` (optional) = { fieldId, projectKey | projectId, issueTypeId, issueKey }
  // for a custom user field's picker: the server then lists only the users
  // that field's context allows (Jira's group / project-role restriction).
  async getUsers(query = '', scope = null) {
    const response = await client.get('/api/users', { params: { query, ...userScopeParams(scope) } });
    return response.data;
  },

  // The @-mention list: only ACTIVE staff accounts (the green chip on
  // Users & Groups). User pickers keep getUsers.
  async getMentionableUsers(query = '') {
    const response = await client.get('/api/users/mentionable', { params: { query } });
    return response.data;
  },

  async getIssueTypes() {
    const response = await client.get('/api/issuetypes');
    return response.data;
  },

  async getProjectIssueTypes(projectKey) {
    const response = await client.get(`/api/project/${projectKey}/issuetypes`);
    return response.data;
  },

  async getProjects() {
    const response = await client.get('/api/projects');
    // Normalize: the server returns a flat array; tolerate a Jira-style
    // {values:[...]} envelope too so a shape regression can never
    // blank-screen every consumer again.
    const d = response.data;
    return Array.isArray(d) ? d : (d?.values || []);
  },

  async getStatuses(siteUrl) {
    const params = siteUrl ? { siteUrl } : {};
    const response = await client.get('/api/statuses', { params });
    return response.data;
  },

  async getIssueTypeStatuses(issueTypeName) {
    const response = await client.get(`/api/issue-type-statuses/${encodeURIComponent(issueTypeName)}`);
    return response.data;
  },

  async getPriorities() {
    const response = await client.get('/api/priorities');
    return response.data;
  },

  async getResolutions() {
    const response = await client.get('/api/resolutions');
    return response.data;
  },

  async getProjectStatuses(projectKey, issueType, siteUrl) {
    const params = {};
    if (issueType) params.issueType = issueType;
    if (siteUrl) params.siteUrl = siteUrl;
    const response = await client.get(`/api/project/${projectKey}/statuses`, { params });
    return response.data;
  },

  async getProjectComponents(projectKey) {
    const response = await client.get(`/api/project/${projectKey}/components`);
    return response.data;
  },

  async getProjectVersions(projectKey) {
    const response = await client.get(`/api/project/${projectKey}/versions`);
    return response.data;
  },

  // Security levels of a project's work-item security scheme →
  // { scheme: { id, name, defaultLevelId } | null, levels: [{ id, name, description }] }.
  // `key` may be a project key or an issue key (resolved to its project).
  async getProjectSecurityLevels(key) {
    const response = await client.get(`/api/project/${encodeURIComponent(key)}/security-levels`);
    return response.data;
  },

  async getLabels() {
    const response = await client.get('/api/labels');
    return response.data;
  },

  async getJqlAutocompleteData(siteUrl) {
    const response = await client.get('/api/jql/autocompletedata', { params: { siteUrl } });
    return response.data;
  },

  async getJqlSuggestions(fieldName, fieldValue, siteUrl) {
    const response = await client.get('/api/jql/suggestions', {
      params: { fieldName, fieldValue, siteUrl }
    });
    return response.data;
  },

  async getIssueLinkTypes(siteUrl) {
    const response = await client.get('/api/issue-link-types', { params: { siteUrl } });
    return response.data;
  },

  async linkIssue(data) {
    const response = await client.post('/api/issue-link', data);
    return response.data;
  },

  async unlinkIssue(linkId) {
    await client.delete(`/api/issue-link/${linkId}`);
  },

  async checkPermissions(issueKey, permissions = 'DELETE_ISSUES', siteUrl = null) {
    const params = { permissions };
    if (siteUrl) params.siteUrl = siteUrl;
    const response = await client.get(`/api/mypermissions/${issueKey}`, { params });
    return response.data;
  },

  // Single-issue delete retired with jira-routes. Bulk-delete via
  // /api/issues/bulk-delete (Stride-backed) still works — pass a single
  // key if you need to delete just one.
  async deleteIssue(key, siteUrl = null) {
    return jiraApi.bulkDeleteIssues([key], siteUrl);
  },

  // Moves the work items to the Recycle bin (restorable). opts.selection:
  // every key being deleted in this run, so a parent sent in an earlier
  // slice than its children is not refused; opts.skipBlocked: delete the
  // rest when some still carry children. → { processed, batchId, deleted, failed }
  async bulkDeleteIssues(keys, siteUrl = null, opts = {}) {
    const response = await client.post('/api/issues/bulk-delete', { keys, siteUrl, ...opts });
    return response.data;
  },

  // → { deletable, blocked: [{ key, children }], missing, refused }
  async planBulkDelete(keys, selection = null) {
    const response = await client.post('/api/issues/bulk-delete/plan', { keys, selection });
    return response.data;
  },

  // The Recycle bin. list → { entries, canPurge, retentionDays };
  // restore({ deletionIds } | { batchId }) → { restored, withParents, refused };
  // purge(deletionIds) → { purged }.
  async listRecycleBin(q = '') {
    const response = await client.get('/api/recycle-bin', { params: q ? { q } : {} });
    return response.data;
  },

  async restoreFromRecycleBin(target) {
    const response = await client.post('/api/recycle-bin/restore', target);
    return response.data;
  },

  async purgeFromRecycleBin(deletionIds) {
    const response = await client.post('/api/recycle-bin/purge', { deletionIds });
    return response.data;
  },

  // The grouped Bulk Edit's plan → { groups: [{ id, projectKey, workTypeName,
  // statusName, keys, items, transitions, fields, readOnly, parentAllowed }], unavailable }
  // Moving to another project. plan → { target, workTypes, workTypeId,
  // statuses, statusKeep, parentTypes, parentRequired, items: [{ key, ok,
  // reason, descendants }], refused }; run → { processed, moved: [{ from, to }], failed }.
  async planBulkMove(keys, targetProjectKey, { workTypeId = null, statusId = null, parentKey = null } = {}) {
    const response = await client.post('/api/issues/bulk-move/plan', { keys, targetProjectKey, workTypeId, statusId, parentKey });
    return response.data;
  },

  async bulkMoveIssues(keys, targetProjectKey, { workTypeId, statusId = null, parentKey = null } = {}) {
    const response = await client.post('/api/issues/bulk-move', { keys, targetProjectKey, workTypeId, statusId, parentKey });
    return response.data;
  },

  async planBulkEdit(keys) {
    const response = await client.post('/api/issues/bulk-edit/plan', { keys });
    return response.data;
  },

  async bulkEditIssues(keys, fields, siteUrl = null) {
    const response = await client.post('/api/issues/bulk-edit', { keys, fields, siteUrl });
    return response.data;
  },

  // Bulk transition. plan → { groups: [{ workflowName, statusId, statusName,
  // keys, transitions }], unavailable }; run → { requested, processed,
  // alreadyThere, failed }. groups: [{ keys, transitionId, toStatusId, fields, comment }].
  async planBulkTransition(keys) {
    const response = await client.post('/api/issues/bulk-transition/plan', { keys });
    return response.data;
  },

  async bulkTransitionIssues(groups) {
    const response = await client.post('/api/issues/bulk-transition', { groups });
    return response.data;
  },

  // Parent changes — the server decides (hierarchy, loops, sub-task
  // project) and writes the parent column or the hierarchy link itself.
  // One item: { key, parentKey, alreadyThere }; a refusal is a 400/404
  // whose errorMessages[0] is the sentence to show.
  async setParent(key, parentKey) {
    const response = await client.put(`/api/issue/${encodeURIComponent(key)}/parent`, { parentKey: parentKey || null });
    return response.data;
  },

  // Many: plan → { parent, items: [{ key, ok, alreadyThere, reason, currentParentKey }], error };
  // run → { requested, processed, alreadyThere, failed }.
  async planBulkParent(keys, parentKey) {
    const response = await client.post('/api/issues/bulk-parent/plan', { keys, parentKey: parentKey || null });
    return response.data;
  },

  async bulkSetParent(keys, parentKey) {
    const response = await client.post('/api/issues/bulk-parent', { keys, parentKey: parentKey || null });
    return response.data;
  },

  // { edit, delete } — may this person bulk change / bulk delete at all
  // (Settings → Bulk Edit Access / Bulk Delete Access; admins always).
  async getBulkAccess() {
    const response = await client.get('/api/issues/bulk-access');
    return response.data;
  },

  async searchIssuePicker(query, currentIssueKey, project, issueTypes, currentJQL) {
    const params = {};
    if (query) params.query = query;
    if (currentIssueKey) params.currentIssueKey = currentIssueKey;
    if (project) params.project = project;
    if (issueTypes) params.issueTypes = issueTypes;
    if (currentJQL) params.currentJQL = currentJQL;
    const response = await client.get('/api/issue-picker', { params });
    return response.data;
  },

  // Smart links: → { cards: { KEY: { key, summary, status, issuetype } } };
  // a key the caller may not see is absent.
  async getIssueCards(keys) {
    const response = await client.post('/api/issues/cards', { keys });
    return response.data;
  },

  // → { legacyJiraHosts: [...] } — the old Jira site(s) a paste rewrites.
  async getSmartLinkConfig() {
    const response = await client.get('/api/smart-links/config');
    return response.data;
  },

  // Walk the descendant tree rooted at `key`, following only the
  // hierarchy edges provided. Returns { keys: [...] } — flat list of
  // everything reachable, excluding the anchor itself. Used to hide
  // already-in-hierarchy candidates from the add-child picker.
  async getDescendantKeys(key, edges) {
    const response = await client.post(`/api/issue/${encodeURIComponent(key)}/descendant-keys`, { edges });
    return response.data;
  },

  // Groups
  // { adminGroup, people } — how many people these groups (+ site admins) cover.
  async groupReach(groups = []) {
    const response = await client.get('/api/groups/reach', { params: { groups: groups.join(',') } });
    return response.data;
  },

  async getGroups(query = '') {
    const response = await client.get('/api/groups', { params: { query } });
    return response.data;
  },

  async getUserGroups() {
    const response = await client.get('/api/user/groups');
    return response.data;
  },

  // Attachments
  async uploadAttachment(issueKey, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await client.post(`/api/issue/${issueKey}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteAttachment(attachmentId) {
    const response = await client.delete(`/api/attachment/${attachmentId}`);
    return response.data;
  },

  getAttachmentContentUrl(attachmentId) {
    return `${API_BASE}/api/attachment-content/${attachmentId}`;
  },

  // The card's thumbnail and the preview's document, as Blobs. Fetched
  // with the Authorization header rather than a ?token= URL: the URL stays
  // the same across token renewals (so the browser can revalidate its
  // copy of the thumbnail instead of downloading it again), and a fetch
  // is not a navigation, so the service worker's app-shell fallback can
  // never answer it.
  async getAttachmentThumbBlob(attachmentId) {
    const response = await client.get(`/api/attachment-thumb/${attachmentId}`, { responseType: 'blob' });
    return response.data;
  },
  async getAttachmentBlob(attachmentId) {
    const response = await client.get(`/api/attachment-content/${attachmentId}`, { responseType: 'blob', timeout: 0 });
    return response.data;
  },

  // Descendants & Aggregates
  async getContainerAggregates(containerId, topKeys, extendConfig, formulas, siteUrl = null, groups = {}, fieldNameMap = null, hydrateFields = null, opts = {}) {
    // Serialize the Map as [alias, fieldId] entries so the server can rebuild it.
    const fieldAliases = fieldNameMap instanceof Map ? Array.from(fieldNameMap.entries()) : null;
    const body = { topKeys, extendConfig, formulas, siteUrl, groups };
    if (fieldAliases && fieldAliases.length > 0) body.fieldAliases = fieldAliases;
    // Structure level of this container's TOP work items — 1 plus however many
    // GROUP tiers apply, which only the client knows. Drives `level` inside the
    // formulas (`if (level = 1, …)`), so it has to travel with the request.
    if (Number.isFinite(opts.baseLevel)) body.baseLevel = opts.baseLevel;
    // Phase 2: when hydrateFields is provided, the backend response includes
    // `descendantsByParent` so the frontend can pre-populate containerChildrenMap
    // and skip per-chevron round-trips. Backend applies a size guard for very
    // large trees — omits the field silently when over threshold.
    if (Array.isArray(hydrateFields) && hydrateFields.length > 0) body.hydrateFields = hydrateFields;
    const response = await client.post(`/api/container-aggregates/${containerId}`, body);
    return response.data;
  },

  // Ask the server to (re)compute a container's stored formula values.
  //
  // Fire-and-forget: returns { queued } immediately and the values arrive in the
  // store, announced over the socket as 'formula-store-updated'. There is no
  // size argument and no force flag — the server computes whole containers, one
  // at a time, and `sig` is what decides whether a compute is needed at all, so
  // there is nothing left for a caller to size, chunk or override.
  async requestFormulaCompute(containerId, topKeys, extendConfig, formulas, groups = {}, fieldNameMap = null, hydrateFields = null, baseLevel = 2, sig = null) {
    const fieldAliases = fieldNameMap instanceof Map ? Array.from(fieldNameMap.entries()) : null;
    const body = { topKeys, extendConfig, formulas, groups };
    if (fieldAliases && fieldAliases.length > 0) body.fieldAliases = fieldAliases;
    if (Number.isFinite(baseLevel)) body.baseLevel = baseLevel;
    if (Array.isArray(hydrateFields) && hydrateFields.length > 0) body.hydrateFields = hydrateFields;
    // The signature of the recipe these values will be computed from. The client
    // sends its own so it can compare against the stored one without a
    // round-trip; the server derives an equivalent when it is absent.
    if (sig) body.sig = sig;
    const response = await client.post(`/api/container-formula-populate/${containerId}`, body);
    return response.data;
  },

  // P3 read path: fetch PRECOMPUTED formula-column values for a container's
  // issues from the store (zebra_stride_work_item_formula). Independent of the
  // heavy aggregates call. Returns { values: { "issueKey:formulaId": {...} },
  // enabled }. When the server feature is off, values is {} and the caller
  // falls back to computing as before.
  async getContainerFormulas(containerId, issueKeys, formulaIds) {
    if (!Array.isArray(issueKeys) || !issueKeys.length || !Array.isArray(formulaIds) || !formulaIds.length) {
      return { values: {}, enabled: false };
    }
    const response = await client.post(`/api/container-formulas/${containerId}`, { issueKeys, formulaIds });
    return response.data;
  },

  // Batch read of the folder/group TOTAL rows for many containers in one call.
  // The header totals are stored values independent of a container's rows, so
  // the folder reads them ALL up front and paints every header instantly instead
  // of one-by-one as each container's rows load. Response: { totals: { [id]: {...} } }.
  async getContainerFormulaTotals(containerIds, formulaIds) {
    if (!Array.isArray(containerIds) || !containerIds.length || !Array.isArray(formulaIds) || !formulaIds.length) {
      return { totals: {} };
    }
    const response = await client.post('/api/container-formula-totals', { containerIds, formulaIds });
    return response.data;
  },

  // Phase 1 fast folder-loading path — one call returns top items,
  // descendants, tree structure, and the field values referenced by
  // `columns`. NO formulas, aggregates, comments, attachments, or link
  // payloads (see modules/folders/server/stride/container-items-handler.js).
  //
  // Frontend callers should use this when the container's insight has
  // zero formula columns; otherwise fall back to getContainerAggregates
  // (which handles formula evaluation).
  //
  // Response: { topItems, descendants, childrenByParent }
  //   - topItems: [{ id, key, self, fields }]
  //   - descendants: [{ id, key, self, fields, parentKey }]
  //   - childrenByParent: { [parentKey]: [childKey, ...] }
  // Batch endpoint for opening a Work Item Screen — replaces the
  // 5-6 waterfall requests (issue + editmeta + comments + transitions +
  // permissions + screen-layout) with a single call. Drops screen-open
  // latency from 500-1000 ms to ~150 ms and eliminates the layout-
  // flash-then-re-render effect.
  //
  // Response: { issue, editmeta, comments, transitions, permissions, screenLayout }
  // Each field is null if the underlying request failed; issue must
  // succeed or the whole call returns an error.
  async getWorkItemScreen(key) {
    const response = await client.get(`/api/work-item/${encodeURIComponent(key)}/screen`);
    return response.data;
  },

  async getContainerItems(containerId, topJql, extendConfig, columns, opts = {}) {
    // descendantsMode:
    //   'eager'    — walk the full descendant tree and return it. Old
    //               Phase 1 behaviour; expensive on big folders.
    //   'top-only' — only fetch top items. Each item carries a
    //               `hasChildren` boolean so the UI can render expand
    //               chevrons. Descendants load lazily via the existing
    //               fetchChildrenForParent path on chevron click.
    //   'skeleton' — the WHOLE tree's shape and none of its values. Rows come
    //               back as { id, key } (+ parentKey) with NO `fields` key at
    //               all, and that absence is the signal a row is not hydrated
    //               yet. Membership is identical to 'eager' — same walk, same
    //               permission prune, same filter pass — so the tree renders
    //               complete and correct; only the cell values are missing,
    //               and hydrateContainerItems below fills them in a viewport
    //               at a time. Measured: 92.3% of an eager payload is field
    //               values, so this is ~21MB where eager is ~281MB.
    const body = {
      topJql, extendConfig, columns,
      descendantsMode: opts.descendantsMode || 'eager',
      // ZQL filter clause(s) to apply to EXTENDED descendants too (Jira parity).
      // Top items are already narrowed by topJql; this lets the server drop
      // extended children that don't match the folder's Filter generator.
      filterJql: opts.filterJql || '',
      // Only meaningful with 'skeleton': the field ids the folder's
      // group/sort/filter rules read, carried for every row because the client
      // cannot place a row without them.
      skeletonFieldIds: opts.skeletonFieldIds || [],
      // Ask for issue links on EVERY row, not just the top ones. Only the
      // Gantt needs this — an arrow is dropped unless both endpoints carry
      // links, and both are usually descendants. Off by default because it
      // widens the server's link query to the whole folder.
      dependencyLinks: !!opts.dependencyLinks,
    };
    const response = await client.post(`/api/container/${containerId}/items`, body);
    return response.data;
  },

  // Field values for ONE viewport's worth of rows, the other half of
  // descendantsMode:'skeleton'. Called again as the user scrolls.
  //
  // The server re-checks visibility on every id and REFUSES an over-large
  // batch rather than truncating it (a short window would render as
  // permanently blank rows with nothing to say why), so the caller must keep
  // batches bounded — hydrationStore does that.
  //
  // Returns { items: [{ id, key, fields }] }. A row the server declines to
  // return is simply absent, and stays a placeholder.
  async hydrateContainerItems(containerId, workItemIds, columns) {
    const response = await client.post(`/api/container/${containerId}/hydrate`, { workItemIds, columns });
    return response.data;
  },

  // Many sections, one request (Phase 2). Each spec mirrors a single
  // getContainerItems call: { containerId, topJql, extendConfig, columns,
  // filterJql, descendantsMode }. Returns { sections: { [containerId]:
  // { status, body } } } — a failed section reports its own status without
  // failing the batch. Used by the folder's background hydration so N
  // collapsed sections cost one round-trip, one permission resolve, and one
  // edge-snapshot prime instead of N of each.
  async getContainerItemsBatch(sections) {
    const response = await client.post('/api/container-items/batch', { sections });
    return response.data;
  },

  // Folder text search. Stride's sub-router intercepts /api/folder-search
  // and serves matches (with their ancestor chains) from Supabase, so the
  // hasMatchInSubtree walk can surface deep matches with their parent row
  // visible.
  async folderSearch(query, siteUrl = null) {
    const response = await client.post('/api/folder-search', { query, siteUrl });
    return response.data;
  },

  // Icon/image URL resolver. Stride payloads emit relative backend paths
  // (/api/icons/..., /api/avatars/...) — the frontend and backend live on
  // DIFFERENT hosts, so a bare relative <img src> would resolve against
  // the frontend origin and 404. Prefix those with API_BASE. Everything
  // else (data: URLs, absolute URLs) passes through untouched.
  getJiraImageUrl(url) {
    if (!url) return null;
    if (url.startsWith('/api/')) return `${API_BASE}${url}`;
    return url;
  },

  // Comments & Changelog
  async getComments(issueKey, siteUrl = null) {
    const params = {};
    if (siteUrl) params.siteUrl = siteUrl;
    const response = await client.get(`/api/issue/${issueKey}/comments`, { params });
    return response.data;
  },

  // `parentId` threads the new comment under an existing one. Threads are
  // flat: replying to a reply attaches to the same root (the server
  // normalises this), so indentation never runs away.
  // opts.internal — an agent's note to other agents. Only a service desk
  // project ever sends it; everywhere else a comment has one audience and
  // the flag is simply absent, which the server reads as customer-visible.
  async addComment(issueKey, body, siteUrl = null, parentId = null, opts = {}) {
    const data = { body };
    if (siteUrl) data.siteUrl = siteUrl;
    if (parentId) data.parentId = String(parentId);
    if (opts.internal) data.internal = true;
    const response = await client.post(`/api/issue/${issueKey}/comment`, data);
    return response.data;
  },

  // Deleting a thread root removes its replies too (server-side).
  async deleteComment(issueKey, commentId) {
    const response = await client.delete(`/api/issue/${issueKey}/comment/${commentId}`);
    return response.data;
  },

  async getChangelog(issueKey, siteUrl = null) {
    const params = {};
    if (siteUrl) params.siteUrl = siteUrl;
    const response = await client.get(`/api/issue/${issueKey}/changelog`, { params });
    return response.data;
  },

  // Project Categories
  async getProjectCategories() {
    const response = await client.get('/api/project-categories');
    return response.data;
  },

  // Jira Issue Type Schemes
  async getIssueTypeSchemes() {
    const response = await client.get('/api/issue-type-schemes');
    return response.data;
  },

  async getIssueTypeSchemeMapping(schemeId) {
    const response = await client.get(`/api/issue-type-schemes/${schemeId}/mapping`);
    return response.data;
  },

  async getProjectIssueTypeScheme(projectKey) {
    const response = await client.get(`/api/project/${projectKey}/issue-type-scheme`);
    return response.data;
  },

  // Batch: resolve many project keys -> schemes in one round-trip.
  // Returns { [projectKey]: { schemeId, schemeName } }.
  async getProjectIssueTypeSchemes(projectKeys) {
    const response = await client.post('/api/project-issue-type-schemes', { projectKeys });
    return response.data;
  },

  // Workflows
  async getWorkflows() {
    const response = await client.get('/api/jira/workflows');
    return response.data;
  },

  async getWorkflowTransitions(workflowName) {
    const response = await client.get(`/api/jira/workflows/${encodeURIComponent(workflowName)}/transitions`);
    return response.data;
  },

  async getIssueWorkflow(projectId, issueTypeId) {
    const response = await client.get(`/api/jira/issue-workflow/${projectId}/${issueTypeId}`);
    return response.data;
  },

  // Gantt PWA-global settings (vacations, dependency rules, vacation colour)
  async getGanttGlobalConfig() {
    const response = await client.get('/api/gantt-global-config');
    return response.data;
  },
  async updateGanttGlobalConfig(patch) {
    const response = await client.put('/api/gantt-global-config', patch);
    return response.data;
  },
  async updateGanttVacations(vacations) {
    const response = await client.put('/api/gantt-global-config/vacations', { vacations });
    return response.data;
  },
  async rebalanceGantt({ structureId, items, startDateField, endDateField, durationField, sourceKey }) {
    const response = await client.post('/api/gantt/rebalance', {
      structureId, items, startDateField, endDateField, durationField, sourceKey,
    });
    return response.data;
  },

  // PWA-wide global settings (date format etc.)
  async getPwaGlobalSettings() {
    const response = await client.get('/api/pwa-global-settings');
    return response.data;
  },
  async updatePwaGlobalSettings(patch) {
    const response = await client.put('/api/pwa-global-settings', patch);
    return response.data;
  },
};

export default jiraApi;
